import React, { useRef, Suspense, useMemo, createElement as h } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Environment, Html } from "@react-three/drei";
import * as THREE from "three";

/**
 * Procedural realistic delivery vans — distinct silhouette per brand.
 * Uses ExtrudeGeometry for the body so we get a real sloped windshield
 * and proper roof curvature. All R3F intrinsics go through createElement
 * (h) to bypass the visual-edits babel plugin.
 */

// Brand-specific design specs — REAL, very visible differentiation
const BRAND_SPECS = {
  trafic: {
    name: "Renault Trafic",
    bodyColor: "#F5F5F5",
    accent: "#FFCC33",
    accentSecondary: "#000000",
    bumperColor: "#1f1f1f",
    cargoStripeColor: "#FFCC33",
    cargoStripeHeight: 0.18,
    totalLen: 5.0,
    height: 1.97,
    width: 1.96,
    hoodLen: 1.05,
    hoodHeight: 0.92,
    windshieldStart: 1.0,
    windshieldTop: 1.5,
    roofY: 1.95,
    roofTaper: 0.06,
    grilleStyle: "horizontal",
    headlightShape: "swept",
    badge: "RENAULT",
    badgeBg: "#FFCC33",
    badgeFg: "#1f1f1f",
    hasRoofRails: false,
    hasRoofSpoiler: true,
  },
  expert: {
    name: "Peugeot Expert",
    bodyColor: "#A8B0B8",
    accent: "#0067B1",
    accentSecondary: "#0a0a0a",
    bumperColor: "#272727",
    cargoStripeColor: "#0067B1",
    cargoStripeHeight: 0.12,
    totalLen: 4.6,
    height: 1.9,
    width: 1.92,
    hoodLen: 0.95,
    hoodHeight: 0.88,
    windshieldStart: 0.95,
    windshieldTop: 1.4,
    roofY: 1.88,
    roofTaper: 0.04,
    grilleStyle: "mesh",
    headlightShape: "boomerang",
    badge: "PEUGEOT",
    badgeBg: "#0067B1",
    badgeFg: "#FFFFFF",
    hasRoofRails: false,
    hasRoofSpoiler: false,
  },
  jumpy: {
    name: "Citroën Jumpy",
    bodyColor: "#FFFFFF",
    accent: "#C8102E",
    accentSecondary: "#1a1a1a",
    bumperColor: "#1a1a1a",
    cargoStripeColor: "#C8102E",
    cargoStripeHeight: 0.08,
    totalLen: 4.6,
    height: 1.9,
    width: 1.92,
    hoodLen: 0.95,
    hoodHeight: 0.88,
    windshieldStart: 0.95,
    windshieldTop: 1.4,
    roofY: 1.88,
    roofTaper: 0.04,
    grilleStyle: "chevron",
    headlightShape: "rectangle",
    badge: "CITROËN",
    badgeBg: "#C8102E",
    badgeFg: "#FFFFFF",
    hasRoofRails: false,
    hasRoofSpoiler: false,
  },
  vivaro: {
    name: "Opel Vivaro",
    bodyColor: "#FFD500",
    accent: "#1a1a1a",
    accentSecondary: "#1a1a1a",
    bumperColor: "#0f0f0f",
    cargoStripeColor: "#1a1a1a",
    cargoStripeHeight: 0.14,
    totalLen: 4.98,
    height: 1.96,
    width: 1.94,
    hoodLen: 1.02,
    hoodHeight: 0.9,
    windshieldStart: 0.98,
    windshieldTop: 1.45,
    roofY: 1.93,
    roofTaper: 0.05,
    grilleStyle: "wing",
    headlightShape: "almond",
    badge: "OPEL",
    badgeBg: "#FAFAFA",
    badgeFg: "#1a1a1a",
    hasRoofRails: true,
    hasRoofSpoiler: false,
  },
  transit: {
    name: "Ford Transit",
    bodyColor: "#1E3A8A",
    accent: "#FFFFFF",
    accentSecondary: "#0a0a0a",
    bumperColor: "#0f0f0f",
    cargoStripeColor: "#FFFFFF",
    cargoStripeHeight: 0.22,
    totalLen: 6.0,
    height: 2.5,
    width: 2.1,
    hoodLen: 1.3,
    hoodHeight: 1.1,
    windshieldStart: 1.2,
    windshieldTop: 1.85,
    roofY: 2.48,
    roofTaper: 0.1,
    grilleStyle: "trapezoid",
    headlightShape: "trapezoid",
    badge: "FORD",
    badgeBg: "#003478",
    badgeFg: "#FFFFFF",
    hasRoofRails: true,
    hasRoofSpoiler: false,
  },
  generic: {
    name: "Fourgon 6m³",
    bodyColor: "#D4D4D8",
    accent: "#52525B",
    accentSecondary: "#1a1a1a",
    bumperColor: "#272727",
    cargoStripeColor: "#3F3F46",
    cargoStripeHeight: 0.06,
    totalLen: 4.3,
    height: 1.85,
    width: 1.85,
    hoodLen: 0.9,
    hoodHeight: 0.85,
    windshieldStart: 0.9,
    windshieldTop: 1.35,
    roofY: 1.82,
    roofTaper: 0.03,
    grilleStyle: "horizontal",
    headlightShape: "rectangle",
    badge: "6m³",
    badgeBg: "#52525B",
    badgeFg: "#FAFAFA",
    hasRoofRails: false,
    hasRoofSpoiler: false,
  },
};

const GLASS_COLOR = "#1f2937";
const TIRE_COLOR = "#0a0a0a";
const RIM_COLOR = "#9ca3af";
const TRIM_COLOR = "#18181B";

// Build the body extrusion from the side profile
function useBodyGeometry(spec) {
  return useMemo(() => {
    const {
      totalLen, height, hoodLen, hoodHeight,
      windshieldStart, windshieldTop, roofY, roofTaper,
    } = spec;

    const halfL = totalLen / 2;
    const groundY = 0.42; // chassis ride height (above wheels)

    const shape = new THREE.Shape();
    // Start at rear-bottom, walk counter-clockwise around the side profile
    shape.moveTo(-halfL, groundY);                            // rear bottom
    shape.lineTo(halfL - hoodLen, groundY);                   // under cabin
    shape.lineTo(halfL - 0.05, groundY + 0.02);               // front lower
    shape.lineTo(halfL, groundY + 0.18);                      // front bumper top
    shape.lineTo(halfL, hoodHeight - 0.05);                   // hood front lip
    shape.lineTo(halfL - 0.04, hoodHeight);                   // hood top front
    shape.lineTo(halfL - hoodLen + windshieldStart, hoodHeight + 0.02); // hood end
    shape.lineTo(halfL - hoodLen + windshieldStart - 0.1, windshieldTop); // windshield top
    shape.lineTo(halfL - hoodLen - 0.05, roofY);              // roof front
    shape.lineTo(-halfL + 0.05, roofY);                       // roof rear (flat)
    shape.lineTo(-halfL, roofY - roofTaper);                  // rear top corner
    shape.lineTo(-halfL, groundY);                            // close
    shape.closePath();

    const geom = new THREE.ExtrudeGeometry(shape, {
      depth: spec.width,
      bevelEnabled: true,
      bevelSize: 0.035,
      bevelThickness: 0.035,
      bevelSegments: 3,
      steps: 1,
    });
    geom.translate(0, 0, -spec.width / 2);
    geom.computeVertexNormals();
    return geom;
  }, [spec]);
}

// Window panel — slim extruded shape on the side
function useWindshieldGeometry(spec) {
  return useMemo(() => {
    const { totalLen, hoodHeight, hoodLen, windshieldStart, windshieldTop, width } = spec;
    const halfL = totalLen / 2;
    const x1 = halfL - hoodLen + windshieldStart - 0.08;
    const x2 = halfL - hoodLen + windshieldStart - 0.08;
    const xTop = halfL - hoodLen + windshieldStart - 0.18;
    const yBottom = hoodHeight + 0.08;
    const yTop = windshieldTop - 0.02;

    const s = new THREE.Shape();
    s.moveTo(x2, yBottom);
    s.lineTo(x1 + 0.02, yBottom);
    s.lineTo(xTop, yTop);
    s.lineTo(xTop - 0.02, yTop);
    s.closePath();

    const g = new THREE.ExtrudeGeometry(s, {
      depth: width - 0.18,
      bevelEnabled: false,
      steps: 1,
    });
    g.translate(0, 0, -(width - 0.18) / 2);
    return g;
  }, [spec]);
}

function Van({ onPick, vanType }) {
  const groupRef = useRef();
  const spec = BRAND_SPECS[vanType] || BRAND_SPECS.generic;
  const { totalLen, height, width, bodyColor, accent } = spec;

  const bodyGeom = useBodyGeometry(spec);
  const windshieldGeom = useWindshieldGeometry(spec);

  const handlePick = (e) => {
    e.stopPropagation();
    if (!onPick) return;
    const p = e.point.clone();
    groupRef.current.worldToLocal(p);
    onPick({ x: p.x, y: p.y, z: p.z });
  };

  const halfL = totalLen / 2;
  const halfW = width / 2;
  const cabinFrontX = halfL - spec.hoodLen + spec.windshieldStart - 0.08; // base of windshield (X)

  // Wheel positions (front near cabin, rear near tail)
  const wheelX_front = halfL - spec.hoodLen - 0.05;
  const wheelX_rear = -halfL + 0.85;
  const wheelY = 0.36;
  const wheels = [
    [wheelX_front, wheelY, halfW + 0.005],
    [wheelX_front, wheelY, -halfW - 0.005],
    [wheelX_rear, wheelY, halfW + 0.005],
    [wheelX_rear, wheelY, -halfW - 0.005],
  ];

  const stdMat = (color, metalness = 0.45, roughness = 0.45) =>
    h("meshStandardMaterial", { color, metalness, roughness });

  // Headlight position & shape
  const hlY = spec.hoodHeight - 0.18;
  const hlX = halfL - 0.04;
  const headlightShapeArgs = {
    swept:      [0.06, 0.18, 0.55],
    boomerang:  [0.07, 0.16, 0.42],
    rectangle:  [0.06, 0.18, 0.36],
    almond:     [0.06, 0.14, 0.46],
    trapezoid:  [0.08, 0.22, 0.55],
  }[spec.headlightShape] || [0.06, 0.18, 0.4];

  // Grille bars
  const grilleY = spec.hoodHeight - 0.45;
  const grilleBars = [];
  const grilleBarCount =
    spec.grilleStyle === "mesh" ? 1 :
    spec.grilleStyle === "trapezoid" ? 2 :
    spec.grilleStyle === "wing" ? 1 :
    spec.grilleStyle === "chevron" ? 0 :
    3;
  for (let i = 0; i < grilleBarCount; i++) {
    const offset = (i - (grilleBarCount - 1) / 2) * 0.08;
    grilleBars.push(
      h("mesh", {
        key: `gbar-${i}`,
        position: [hlX + 0.005, grilleY + offset, 0],
      },
        h("boxGeometry", { args: [0.02, 0.035, width - 0.55] }),
        h("meshStandardMaterial", { color: accent, metalness: 0.6, roughness: 0.3 })
      )
    );
  }

  return h(
    "group",
    { ref: groupRef, position: [0, 0, 0] },

    // === Main body (single extruded mesh) ===
    h(
      "mesh",
      {
        geometry: bodyGeom,
        onPointerDown: handlePick,
        castShadow: true,
        receiveShadow: true,
      },
      h("meshStandardMaterial", {
        color: bodyColor,
        metalness: 0.55,
        roughness: 0.35,
      })
    ),

    // === Windshield (tinted glass following the slope) ===
    h(
      "mesh",
      { geometry: windshieldGeom, position: [0.005, 0, 0] },
      h("meshPhysicalMaterial", {
        color: GLASS_COLOR,
        metalness: 0.1,
        roughness: 0.08,
        transmission: 0.3,
        transparent: true,
        opacity: 0.85,
      })
    ),

    // === Side windows (cabin) ===
    h(
      "mesh",
      {
        position: [
          halfL - spec.hoodLen - 0.05,
          spec.hoodHeight + 0.45,
          halfW + 0.012,
        ],
      },
      h("boxGeometry", { args: [0.85, 0.5, 0.02] }),
      h("meshPhysicalMaterial", {
        color: GLASS_COLOR,
        metalness: 0.1,
        roughness: 0.08,
        transmission: 0.25,
        transparent: true,
        opacity: 0.85,
      })
    ),
    h(
      "mesh",
      {
        position: [
          halfL - spec.hoodLen - 0.05,
          spec.hoodHeight + 0.45,
          -halfW - 0.012,
        ],
      },
      h("boxGeometry", { args: [0.85, 0.5, 0.02] }),
      h("meshPhysicalMaterial", {
        color: GLASS_COLOR,
        metalness: 0.1,
        roughness: 0.08,
        transmission: 0.25,
        transparent: true,
        opacity: 0.85,
      })
    ),

    // === Door split lines (suggestive seams) ===
    // Side sliding door seam
    h("mesh",
      { position: [0.2, spec.roofY / 2, halfW + 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),
    h("mesh",
      { position: [-0.85, spec.roofY / 2, halfW + 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),
    h("mesh",
      { position: [0.2, spec.roofY / 2, -halfW - 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),
    h("mesh",
      { position: [-0.85, spec.roofY / 2, -halfW - 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),

    // Cabin door seam (between cabin and cargo)
    h("mesh",
      { position: [cabinFrontX - 0.95, spec.roofY / 2, halfW + 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),
    h("mesh",
      { position: [cabinFrontX - 0.95, spec.roofY / 2, -halfW - 0.012] },
      h("boxGeometry", { args: [0.015, spec.roofY - 0.5, 0.005] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),

    // Rear cargo doors split (vertical centerline)
    h("mesh",
      { position: [-halfL - 0.005, spec.roofY / 2, 0] },
      h("boxGeometry", { args: [0.005, spec.roofY - 0.5, 0.018] }),
      stdMat(TRIM_COLOR, 0.2, 0.7)
    ),

    // Door handles (driver + passenger)
    h("mesh",
      { position: [halfL - spec.hoodLen - 0.2, spec.hoodHeight + 0.12, halfW + 0.025] },
      h("boxGeometry", { args: [0.18, 0.04, 0.03] }),
      stdMat("#27272A", 0.7, 0.3)
    ),
    h("mesh",
      { position: [halfL - spec.hoodLen - 0.2, spec.hoodHeight + 0.12, -halfW - 0.025] },
      h("boxGeometry", { args: [0.18, 0.04, 0.03] }),
      stdMat("#27272A", 0.7, 0.3)
    ),
    // Sliding side door handle
    h("mesh",
      { position: [-0.45, spec.hoodHeight + 0.12, halfW + 0.025] },
      h("boxGeometry", { args: [0.18, 0.04, 0.03] }),
      stdMat("#27272A", 0.7, 0.3)
    ),

    // === Front bumper ===
    h("mesh",
      { position: [halfL + 0.02, 0.55, 0] },
      h("boxGeometry", { args: [0.12, 0.28, width - 0.04] }),
      stdMat(spec.bumperColor, 0.3, 0.6)
    ),
    // Bumper lower lip
    h("mesh",
      { position: [halfL + 0.02, 0.42, 0] },
      h("boxGeometry", { args: [0.1, 0.08, width - 0.18] }),
      stdMat(spec.bumperColor, 0.3, 0.6)
    ),

    // === Big cargo side stripe (brand color) — major visual cue ===
    h("mesh",
      { position: [(-halfL + (halfL - spec.hoodLen - 0.95)) / 2, spec.hoodHeight + 0.1, halfW + 0.013] },
      h("boxGeometry", { args: [Math.abs(-halfL - (halfL - spec.hoodLen - 0.95)) - 0.1, spec.cargoStripeHeight, 0.01] }),
      h("meshStandardMaterial", { color: spec.cargoStripeColor, metalness: 0.3, roughness: 0.5, emissive: spec.cargoStripeColor, emissiveIntensity: 0.05 })
    ),
    h("mesh",
      { position: [(-halfL + (halfL - spec.hoodLen - 0.95)) / 2, spec.hoodHeight + 0.1, -halfW - 0.013] },
      h("boxGeometry", { args: [Math.abs(-halfL - (halfL - spec.hoodLen - 0.95)) - 0.1, spec.cargoStripeHeight, 0.01] }),
      h("meshStandardMaterial", { color: spec.cargoStripeColor, metalness: 0.3, roughness: 0.5, emissive: spec.cargoStripeColor, emissiveIntensity: 0.05 })
    ),
    // Rear cargo stripe band (across rear doors)
    h("mesh",
      { position: [-halfL - 0.013, spec.hoodHeight + 0.1, 0] },
      h("boxGeometry", { args: [0.01, spec.cargoStripeHeight, width - 0.1] }),
      h("meshStandardMaterial", { color: spec.cargoStripeColor, metalness: 0.3, roughness: 0.5 })
    ),

    // === Roof rails (brand-specific) ===
    spec.hasRoofRails ? h("group", { key: "roof-rails" },
      h("mesh", { position: [-halfL / 2 + 0.2, spec.roofY + 0.08, halfW - 0.18] },
        h("boxGeometry", { args: [totalLen * 0.6, 0.05, 0.05] }),
        stdMat("#1a1a1a", 0.7, 0.3)
      ),
      h("mesh", { position: [-halfL / 2 + 0.2, spec.roofY + 0.08, -halfW + 0.18] },
        h("boxGeometry", { args: [totalLen * 0.6, 0.05, 0.05] }),
        stdMat("#1a1a1a", 0.7, 0.3)
      ),
      // Rail supports (4 mounts)
      ...[0.2, 0.6, 1.0, 1.4].map((t, i) => h("mesh", {
        key: `rail-mount-${i}`,
        position: [-halfL / 2 + 0.2 + (t - 0.8) * totalLen * 0.35, spec.roofY + 0.04, halfW - 0.18],
      },
        h("boxGeometry", { args: [0.06, 0.04, 0.04] }),
        stdMat("#0f0f0f", 0.7, 0.3)
      )),
      ...[0.2, 0.6, 1.0, 1.4].map((t, i) => h("mesh", {
        key: `rail-mount-r-${i}`,
        position: [-halfL / 2 + 0.2 + (t - 0.8) * totalLen * 0.35, spec.roofY + 0.04, -halfW + 0.18],
      },
        h("boxGeometry", { args: [0.06, 0.04, 0.04] }),
        stdMat("#0f0f0f", 0.7, 0.3)
      ))
    ) : null,

    // === Roof spoiler (Trafic-style aero) ===
    spec.hasRoofSpoiler ? h("mesh",
      { position: [-halfL + 0.08, spec.roofY + 0.04, 0], rotation: [0, 0, 0.05] },
      h("boxGeometry", { args: [0.4, 0.08, width - 0.2] }),
      stdMat(spec.bodyColor, 0.5, 0.4)
    ) : null,

    // === Grille ===
    h("mesh",
      { position: [hlX + 0.001, grilleY, 0] },
      h("boxGeometry", { args: [0.02, 0.22, width - 0.45] }),
      stdMat("#0a0a0a", 0.4, 0.6)
    ),
    ...grilleBars,

    // Chevron grille (Citroën-style)
    spec.grilleStyle === "chevron" ? h("group", { key: "chevron" },
      h("mesh",
        { position: [hlX + 0.012, grilleY + 0.02, 0.08], rotation: [0, 0, 0.3] },
        h("boxGeometry", { args: [0.015, 0.03, 0.18] }),
        h("meshStandardMaterial", { color: accent, metalness: 0.7, roughness: 0.2 })
      ),
      h("mesh",
        { position: [hlX + 0.012, grilleY + 0.02, -0.08], rotation: [0, 0, -0.3] },
        h("boxGeometry", { args: [0.015, 0.03, 0.18] }),
        h("meshStandardMaterial", { color: accent, metalness: 0.7, roughness: 0.2 })
      )
    ) : null,

    // Brand badge in center of grille (small block with letter)
    h("mesh",
      { position: [hlX + 0.015, grilleY + 0.02, 0] },
      h("boxGeometry", { args: [0.02, 0.09, 0.14] }),
      h("meshStandardMaterial", { color: accent, metalness: 0.85, roughness: 0.15, emissive: accent, emissiveIntensity: 0.15 })
    ),

    // === Headlights (cluster) ===
    // Left (driver side, +Z)
    h("group", { position: [hlX, hlY, halfW - 0.32] },
      // Housing
      h("mesh", null,
        h("boxGeometry", { args: [headlightShapeArgs[0] + 0.02, headlightShapeArgs[1] + 0.04, headlightShapeArgs[2] + 0.04] }),
        stdMat("#18181B", 0.4, 0.4)
      ),
      // Lens
      h("mesh", { position: [0.012, 0, 0] },
        h("boxGeometry", { args: headlightShapeArgs }),
        h("meshPhysicalMaterial", {
          color: "#fef3c7",
          emissive: "#fde68a",
          emissiveIntensity: 0.55,
          metalness: 0.1,
          roughness: 0.1,
          transmission: 0.4,
          transparent: true,
        })
      ),
      // LED accent strip
      h("mesh", { position: [0.018, headlightShapeArgs[1] / 2 + 0.005, 0] },
        h("boxGeometry", { args: [0.015, 0.015, headlightShapeArgs[2] - 0.05] }),
        h("meshStandardMaterial", { color: "#fafafa", emissive: "#ffffff", emissiveIntensity: 0.9 })
      ),
    ),
    // Right
    h("group", { position: [hlX, hlY, -halfW + 0.32] },
      h("mesh", null,
        h("boxGeometry", { args: [headlightShapeArgs[0] + 0.02, headlightShapeArgs[1] + 0.04, headlightShapeArgs[2] + 0.04] }),
        stdMat("#18181B", 0.4, 0.4)
      ),
      h("mesh", { position: [0.012, 0, 0] },
        h("boxGeometry", { args: headlightShapeArgs }),
        h("meshPhysicalMaterial", {
          color: "#fef3c7",
          emissive: "#fde68a",
          emissiveIntensity: 0.55,
          metalness: 0.1,
          roughness: 0.1,
          transmission: 0.4,
          transparent: true,
        })
      ),
      h("mesh", { position: [0.018, headlightShapeArgs[1] / 2 + 0.005, 0] },
        h("boxGeometry", { args: [0.015, 0.015, headlightShapeArgs[2] - 0.05] }),
        h("meshStandardMaterial", { color: "#fafafa", emissive: "#ffffff", emissiveIntensity: 0.9 })
      ),
    ),

    // === Rear bumper & lights ===
    h("mesh",
      { position: [-halfL - 0.025, 0.55, 0] },
      h("boxGeometry", { args: [0.1, 0.28, width - 0.04] }),
      stdMat(spec.bumperColor, 0.3, 0.6)
    ),
    // Tail lights (vertical bars on cargo door corners)
    h("mesh",
      { position: [-halfL - 0.005, spec.roofY - 0.5, halfW - 0.22] },
      h("boxGeometry", { args: [0.025, 0.45, 0.18] }),
      h("meshStandardMaterial", { color: "#dc2626", emissive: "#ef4444", emissiveIntensity: 0.55, metalness: 0.2, roughness: 0.4 })
    ),
    h("mesh",
      { position: [-halfL - 0.005, spec.roofY - 0.5, -halfW + 0.22] },
      h("boxGeometry", { args: [0.025, 0.45, 0.18] }),
      h("meshStandardMaterial", { color: "#dc2626", emissive: "#ef4444", emissiveIntensity: 0.55, metalness: 0.2, roughness: 0.4 })
    ),
    // Indicator amber on tail lights
    h("mesh",
      { position: [-halfL - 0.008, spec.roofY - 0.75, halfW - 0.22] },
      h("boxGeometry", { args: [0.025, 0.08, 0.18] }),
      h("meshStandardMaterial", { color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.5 })
    ),
    h("mesh",
      { position: [-halfL - 0.008, spec.roofY - 0.75, -halfW + 0.22] },
      h("boxGeometry", { args: [0.025, 0.08, 0.18] }),
      h("meshStandardMaterial", { color: "#fbbf24", emissive: "#f59e0b", emissiveIntensity: 0.5 })
    ),

    // === Rear license plate ===
    h("mesh",
      { position: [-halfL - 0.008, 0.78, 0] },
      h("boxGeometry", { args: [0.012, 0.13, 0.42] }),
      h("meshStandardMaterial", { color: "#FAFAFA", metalness: 0.1, roughness: 0.6 })
    ),

    // === Wheel arch flares ===
    ...wheels.map((pos, i) =>
      h("mesh", {
        key: `arch-${i}`,
        position: [pos[0], pos[1] + 0.08, pos[2] * 0.93],
      },
        h("torusGeometry", { args: [0.42, 0.08, 8, 24, Math.PI] }),
        h("meshStandardMaterial", { color: "#1a1a1a", metalness: 0.2, roughness: 0.8 })
      )
    ),

    // === Wheels (tire + rim + hub) ===
    ...wheels.map((pos, i) =>
      h("group", { key: `wheel-${i}`, position: pos },
        // Tire
        h("mesh",
          { rotation: [Math.PI / 2, 0, 0] },
          h("cylinderGeometry", { args: [0.36, 0.36, 0.22, 32] }),
          stdMat(TIRE_COLOR, 0.05, 0.95)
        ),
        // Inner sidewall band
        h("mesh",
          { rotation: [Math.PI / 2, 0, 0] },
          h("cylinderGeometry", { args: [0.25, 0.25, 0.225, 32] }),
          stdMat("#1c1c1c", 0.1, 0.85)
        ),
        // Rim (5-spoke alloy effect via a disc)
        h("mesh",
          { rotation: [Math.PI / 2, 0, 0], position: [0, 0, pos[2] > 0 ? 0.001 : -0.001] },
          h("cylinderGeometry", { args: [0.22, 0.22, 0.23, 16] }),
          stdMat(RIM_COLOR, 0.85, 0.2)
        ),
        // Hub cap
        h("mesh",
          { rotation: [Math.PI / 2, 0, 0], position: [0, 0, pos[2] > 0 ? 0.005 : -0.005] },
          h("cylinderGeometry", { args: [0.06, 0.06, 0.24, 16] }),
          stdMat("#52525B", 0.9, 0.15)
        ),
      )
    ),

    // === Side mirrors ===
    // Arm + housing each side
    h("group", { position: [halfL - spec.hoodLen + 0.05, spec.hoodHeight + 0.55, halfW + 0.16] },
      h("mesh", { position: [0, 0, -0.08] },
        h("boxGeometry", { args: [0.04, 0.05, 0.16] }),
        stdMat(bodyColor, 0.4, 0.5)
      ),
      h("mesh", null,
        h("boxGeometry", { args: [0.16, 0.22, 0.1] }),
        stdMat(bodyColor, 0.4, 0.5)
      ),
      h("mesh", { position: [0.082, 0, 0] },
        h("boxGeometry", { args: [0.005, 0.18, 0.085] }),
        h("meshPhysicalMaterial", { color: "#0f172a", metalness: 0.9, roughness: 0.05, transmission: 0.1 })
      ),
    ),
    h("group", { position: [halfL - spec.hoodLen + 0.05, spec.hoodHeight + 0.55, -halfW - 0.16] },
      h("mesh", { position: [0, 0, 0.08] },
        h("boxGeometry", { args: [0.04, 0.05, 0.16] }),
        stdMat(bodyColor, 0.4, 0.5)
      ),
      h("mesh", null,
        h("boxGeometry", { args: [0.16, 0.22, 0.1] }),
        stdMat(bodyColor, 0.4, 0.5)
      ),
      h("mesh", { position: [0.082, 0, 0] },
        h("boxGeometry", { args: [0.005, 0.18, 0.085] }),
        h("meshPhysicalMaterial", { color: "#0f172a", metalness: 0.9, roughness: 0.05, transmission: 0.1 })
      ),
    ),

    // === Roof antenna ===
    h("mesh",
      { position: [halfL - spec.hoodLen - 0.6, spec.roofY + 0.07, 0] },
      h("cylinderGeometry", { args: [0.015, 0.015, 0.14, 8] }),
      stdMat("#27272A", 0.6, 0.3)
    ),

    // === Brand emblem removed — was an HTML overlay that bled through the body
    // when rotating. Brand is still identifiable via body color, cargo stripe color,
    // and the "Modèle 3D" label in the top-left of the viewer.

    // === Side decal removed — model name already shown in top-left HUD ===
  );
}

function ImpactMarker({ position, severity, onClick, isActive }) {
  const colors = { mineur: "#F59E0B", majeur: "#EA580C", critique: "#DC2626" };
  const color = colors[severity] || "#DC2626";

  // Push the marker slightly outward from the van center so it doesn't
  // z-fight with the body surface and never appears through the back side.
  const center = { x: 0, y: 1, z: 0 };
  const dx = position.x - center.x;
  const dy = position.y - center.y;
  const dz = position.z - center.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
  const offset = 0.05;
  const px = position.x + (dx / len) * offset;
  const py = position.y + (dy / len) * offset;
  const pz = position.z + (dz / len) * offset;

  return h(
    "group",
    { position: [px, py, pz] },
    h(
      "mesh",
      { rotation: [Math.PI / 2, 0, 0] },
      h("ringGeometry", { args: [0.08, 0.13, 32] }),
      h("meshBasicMaterial", {
        color,
        transparent: true,
        opacity: 0.85,
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: true,
      })
    ),
    h(
      "mesh",
      {
        onPointerDown: (e) => {
          e.stopPropagation();
          onClick && onClick();
        },
      },
      h("sphereGeometry", { args: [0.07, 16, 16] }),
      h("meshStandardMaterial", {
        color,
        emissive: color,
        emissiveIntensity: isActive ? 0.9 : 0.45,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -4,
      })
    ),
    h(
      Html,
      {
        distanceFactor: 8,
        position: [0, 0.22, 0],
        center: true,
        occlude: true,
      },
      h(
        "div",
        {
          className:
            "px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white pointer-events-none",
          style: { backgroundColor: color, fontFamily: "IBM Plex Mono" },
        },
        severity[0].toUpperCase()
      )
    )
  );
}

function CanvasContent({ vanType, impacts, onPick, activeImpactId, onSelectImpact }) {
  return h(
    React.Fragment,
    null,
    h("ambientLight", { intensity: 0.5 }),
    h("directionalLight", { position: [6, 9, 5], intensity: 1.0, castShadow: true }),
    h("directionalLight", { position: [-5, 4, -3], intensity: 0.35 }),
    h("hemisphereLight", { args: ["#dbeafe", "#1f2937", 0.3] }),
    h(
      Suspense,
      { fallback: null },
      h(Environment, { preset: "warehouse" })
    ),
    h(Van, { onPick, vanType }),
    ...impacts.map((imp) =>
      h(ImpactMarker, {
        key: imp.id,
        position: imp.position,
        severity: imp.severity,
        isActive: activeImpactId === imp.id,
        onClick: () => onSelectImpact && onSelectImpact(imp.id),
      })
    ),
    h(ContactShadows, {
      position: [0, 0, 0],
      opacity: 0.55,
      scale: 12,
      blur: 2.4,
      far: 4,
    }),
    h(OrbitControls, {
      enablePan: false,
      minDistance: 3,
      maxDistance: 14,
      minPolarAngle: Math.PI / 6,
      maxPolarAngle: Math.PI / 2.05,
    })
  );
}

function VanViewerComponent({
  vanType = "trafic",
  impacts = [],
  onAddImpact,
  activeImpactId,
  onSelectImpact,
}) {
  const handlePick = (pos) => {
    if (onAddImpact) onAddImpact(pos);
  };
  const spec = BRAND_SPECS[vanType] || BRAND_SPECS.generic;

  return (
    <div className="relative w-full h-full bg-zinc-50 brutalist-border touch-none" data-testid="van-3d-viewer">
      <Canvas
        camera={{ position: [6, 3.2, 6], fov: 42 }}
        shadows
        dpr={[1, 2]}
        gl={{ preserveDrawingBuffer: true, antialias: true, powerPreference: "high-performance" }}
        flat
      >
        {h("color", { attach: "background", args: ["#FAFAFA"] })}
        <CanvasContent
          vanType={vanType}
          impacts={impacts}
          onPick={handlePick}
          activeImpactId={activeImpactId}
          onSelectImpact={onSelectImpact}
        />
      </Canvas>
      <div className="absolute top-2 left-2 bg-white border border-zinc-900 px-2 py-1 md:px-3 md:py-2 pointer-events-none" data-testid="van-model-label">
        <div className="label-tag">Modèle 3D</div>
        <div className="text-xs md:text-sm font-bold uppercase tracking-wide">{spec.name}</div>
      </div>
      <div className="absolute bottom-2 left-2 bg-zinc-900 text-white px-2 py-1 md:px-3 md:py-2 pointer-events-none max-w-[60%]">
        <div className="text-[10px] md:text-xs font-mono uppercase tracking-widest">
          Toucher pour marquer un impact
        </div>
      </div>
      <div className="absolute bottom-2 right-2 bg-white border border-zinc-900 px-2 py-1 md:px-3 md:py-2 pointer-events-none" data-testid="van-impact-count">
        <div className="label-tag">Impacts</div>
        <div className="text-base md:text-lg font-black">{impacts.length}</div>
      </div>
    </div>
  );
}

// Memoize so the Canvas does NOT re-mount when parents re-render (file picker,
// dialog state, etc.). This prevents the "flash" on mobile when iOS resumes
// the page after the native camera/gallery picker closes.
const VanViewer = React.memo(VanViewerComponent, (prev, next) => {
  return (
    prev.vanType === next.vanType &&
    prev.activeImpactId === next.activeImpactId &&
    prev.impacts === next.impacts &&
    prev.onAddImpact === next.onAddImpact &&
    prev.onSelectImpact === next.onSelectImpact
  );
});

export default VanViewer;
